import SubscriptionTypeEnum from '@constants/enums/subscription/type';

import { formatSubscriptionPeriod } from '@packages/rate-plans/utils/formatSubscriptionPeriod';

import i18n from '@plugins/i18n';

import Collection from '@/models/Collection';
import Subscription from '@/models/Subscription';

export class SystemSubscriptionCollection extends Collection {
    static type = 'SystemSubscriptionCollection';
    static endpoint = Subscription.api.index;
    static model = Subscription;

    get default_headers() {
        return ['billing_group_id', 'subscription_type', 'rate_plan_id', 'date_range', 'adviser_id', 'investors_count'];
    }

    get export_headers() {
        return this.default_headers;
    }

    get headers() {
        return [
            this.createHeader('billing_group_id', i18n.t('_page.rate_plan.billing_group'), {
                width: '16rem',
                filterable: {
                    operators: this.getOperators(['eq']),
                    component: () => import('@/components/fields/BillingGroup.vue')
                },
                transform: data => data.item?.billing_group?.name || i18n.t('unnamed')
            }),
            this.createHeader('subscription_type', i18n.t('_page.data_entry_subscriptions.type'), {
                format: 'enum',
                filterable: true,
                sortable: 'rate_plan_type',
                enum: SubscriptionTypeEnum,
                enumA11yKey: 'enums.subscription_type',
                width: '9rem'
            }),
            this.createHeader('rate_plan_id', i18n.t('_page.data_entry_subscriptions.rate_plan'), {
                width: '16rem',
                filterable: {
                    operators: this.getOperators(['eq']),
                    component: () => import('@/components/fields/RatePlan.vue')
                },
                transform: data => {
                    const plan = data.item?.rate_plan;
                    const name = plan?.name || i18n.t('unnamed');

                    if (plan?.status === 'DEPRECATED') {
                        return `${name} (${i18n.t('enums.rate_plan_status.DEPRECATED')})`;
                    }

                    return name;
                }
            }),
            this.createHeader('date_range', i18n.t('_page.data_entry_subscriptions.effective_period'), {
                width: '14rem',
                filterable: {
                    operators: this.getOperators(['gte', 'lte']),
                    component: () => import('@/components/fields/Date.vue')
                },
                transform: data => formatSubscriptionPeriod(data.item?.date_range) || '-'
            }),
            this.createHeader('adviser_id', i18n.t('adviser'), {
                width: '14rem',
                filterable: {
                    operators: this.getOperators(['eq']),
                    component: () => import('@/components/fields/Adviser.vue')
                },
                transform: data => data.item?.adviser?.name
            }),
            this.createHeader('investors_count', i18n.t('_page.data_entry_subscriptions.investors_count'), {
                format: 'number',
                width: '8rem',
                filterable: false,
                sortable: false
            }),
            this.createHeader('gi_ref', i18n.t('gi_ref'), {
                width: '8rem',
                filterable: false,
                active: false
            }),
            this.createHeader('investor_id', i18n.tc('investor'), {
                width: '12rem',
                active: false,
                filterable: {
                    operators: this.getOperators(['eq']),
                    component: () => import('@/components/fields/Investor.vue')
                },
                transform: data => data.item?.investor_id
            })
        ];
    }

    transformFilters(filters = {}) {
        const transformed = { ...filters };

        if (Object.prototype.hasOwnProperty.call(transformed, 'date_range')) {
            transformed.start_date = transformed.date_range;
            delete transformed.date_range;
        }

        if (Object.prototype.hasOwnProperty.call(transformed, 'subscription_type')) {
            transformed.rate_plan_type = transformed.subscription_type;
            delete transformed.subscription_type;
        }

        return transformed;
    }
}

export default SystemSubscriptionCollection;
